import React from "react";
import LegalLayout, { H2, H3, P, UL, Note, Table } from "../components/composite/LegalLayout";
import usePageMeta from "../hooks/usePageMeta";

const TOC = [
  { id: "who-we-are", label: "Who we are" },
  { id: "roles", label: "Our role: controller and processor" },
  { id: "what-we-collect", label: "Information we collect" },
  { id: "monitored", label: "Information about monitored accounts" },
  { id: "how-we-use", label: "How we use information & legal bases" },
  { id: "ai", label: "Automated analysis and AI" },
  { id: "sharing", label: "Sharing and sub-processors" },
  { id: "transfers", label: "International transfers" },
  { id: "retention", label: "Data retention" },
  { id: "security", label: "Security" },
  { id: "your-rights", label: "Your privacy rights" },
  { id: "monitored-rights", label: "Rights of monitored individuals" },
  { id: "cookies", label: "Cookies and similar technologies" },
  { id: "children", label: "Children" },
  { id: "changes", label: "Changes to this policy" },
  { id: "contact", label: "Contact us" },
];

export default function PrivacyPolicy() {
  usePageMeta({
    title: "Privacy Policy · RepCheck",
    description:
      "How RepCheck collects, uses, shares and protects personal information - including information about the social media accounts our customers monitor.",
  });

  return (
    <LegalLayout
      title="Privacy Policy"
      updated="4 September 2026"
      toc={TOC}
      summary="This policy explains what personal information RepCheck collects, why we collect it, who we share it with, and the choices and rights you have. It covers both the people who use RepCheck and the people whose public social media activity is analysed through the service."
    >
      <H2 id="who-we-are">1. Who we are</H2>
      <P>
        RepCheck ("RepCheck", "we", "us") provides a reputation-monitoring service that analyses publicly
        available social media content and flags material that may present a reputational risk. This policy
        applies to the RepCheck website at getrepcheck.com, the RepCheck web application, the RepGuard browser
        extension, and related services (together, the "Service").
      </P>
      <P>
        The data controller for the purposes of applicable data protection law is RepCheck, operating from the
        United States. You can reach us about anything in this policy at{" "}
        <a href="mailto:privacy@getrepcheck.com" className="text-brand-600 underline underline-offset-2 hover:text-brand-700">privacy@getrepcheck.com</a>.
      </P>

      <H2 id="roles">2. Our role: controller and processor</H2>
      <P>
        RepCheck acts in two different capacities, and this distinction matters for your rights:
      </P>
      <UL>
        <li>
          <strong>As a controller</strong> - for information about the people who register for and use the
          Service (account details, billing information, support correspondence and usage data). We decide how
          and why that information is processed.
        </li>
        <li>
          <strong>As a processor</strong> - for the monitoring data our customers direct us to collect and
          analyse, including the social media accounts they choose to monitor and the public posts associated
          with those accounts. Our customer decides which accounts to monitor and why; we process that data on
          their documented instructions. Where our customer is subject to the GDPR or similar law, that
          customer is the controller and is responsible for having a lawful basis for the monitoring.
        </li>
      </UL>
      <P>
        If you believe your information is being processed through RepCheck by one of our customers and you want
        it removed, see <a href="#monitored-rights" className="text-brand-600 hover:text-brand-700">Section 12</a>.
      </P>

      <H2 id="what-we-collect">3. Information we collect</H2>

      <H3>3.1 Information you give us</H3>
      <UL>
        <li><strong>Account information</strong> - your name, email address, password (stored only as a salted hash, never in readable form), and optionally your phone number.</li>
        <li><strong>Preferences</strong> - your timezone (used to schedule monitoring at your local time) and notification settings.</li>
        <li><strong>Workspace data</strong> - the clients or brands you set up, and the social media handles you ask us to monitor.</li>
        <li><strong>Support and feedback</strong> - messages, bug reports and correspondence you send us.</li>
        <li><strong>Billing information</strong> - where paid plans apply, plan and subscription details. Card details are handled by our payment provider and are not stored by RepCheck.</li>
      </UL>

      <H3>3.2 Information we collect automatically</H3>
      <UL>
        <li><strong>Authentication data</strong> - a secure, HttpOnly session cookie that keeps you signed in.</li>
        <li><strong>Service and diagnostic logs</strong> - technical records such as IP address, timestamps, request paths and error information, generated when you use the Service. We use these to operate, secure and debug the Service.</li>
      </UL>
      <Note>
        We do not use third-party advertising networks, analytics trackers, session-replay tools or embedded
        chat widgets on the Service, and we do not build advertising profiles about you.
      </Note>

      <H2 id="monitored">4. Information about monitored accounts</H2>
      <P>
        This is the most important section of this policy, because RepCheck necessarily processes information
        about people who are not our customers.
      </P>
      <P>
        When a customer asks us to monitor a social media account, we collect information that is publicly
        visible on that platform for that account. Depending on the platform, this can include:
      </P>
      <UL>
        <li>the account handle, display name, profile picture URL, biography, verified status, follower count and profile link;</li>
        <li>the text of public posts, the time they were published, and their public URLs;</li>
        <li>public engagement figures such as like and reply counts;</li>
        <li>images and other media attached to public posts;</li>
        <li>the text of public replies to those posts, when a customer asks us to read how an audience is responding, together with the reply's public engagement figures and the time it was published; and</li>
        <li>information we generate about that content: a risk score, risk categories, a short written explanation of why content was flagged, and, for replies, an assessment of the feeling expressed, whether the reply supports or opposes the monitored account, and whether it carries something that needs attention such as a legal threat or an allegation.</li>
      </UL>
      <P>
        <strong>We do not store the identity of the people who write those replies.</strong> A reply is saved with
        its text and a one-way keyed hash of the author's handle, which lets us count one person once without
        holding their handle in a readable form. We do not build profiles of repliers, and we do not use these
        records to identify anyone.
      </P>
      <P>
        We collect this information by retrieving publicly accessible pages from the relevant platform. We do
        not access private accounts, private messages, follower-only content, or any content that is not
        publicly viewable, and we do not attempt to circumvent technical access restrictions on a person's
        private content.
      </P>
      <P>
        Because this information is collected from a platform rather than from the individual directly, it is
        generally impossible for us to contact every affected person individually. Where the GDPR applies, we
        rely on the exemption for disproportionate effort in Article 14(5)(b) and publish this policy as the
        transparency measure required by that Article. Our customers remain responsible for ensuring their
        monitoring is lawful, proportionate and, where required, disclosed to the people concerned.
      </P>
      <Note>
        <strong>Risk scores are automated opinions, not statements of fact.</strong> A flag produced by RepCheck
        means content matched patterns associated with a risk category. It is not a finding that any person has
        done anything wrong, and it should never be treated as one.
      </Note>

      <H2 id="how-we-use">5. How we use information, and our legal bases</H2>
      <Table
        head={["What we do", "Why", "Legal basis (GDPR)"]}
        rows={[
          ["Create and administer your account; provide the Service", "To deliver what you signed up for", "Performance of a contract"],
          ["Collect and analyse public posts for accounts you choose to monitor", "To deliver the monitoring you requested", "Performance of a contract (with you); legitimate interests, or your own lawful basis as controller, for the monitored individual"],
          ["Send alerts and service emails (e.g. password reset, risk alerts)", "To notify you of results and account activity", "Performance of a contract; legitimate interests"],
          ["Secure, debug, monitor and improve the Service", "To keep the Service working and safe", "Legitimate interests"],
          ["Handle support requests", "To help you", "Performance of a contract; legitimate interests"],
          ["Comply with law; respond to lawful requests; establish or defend legal claims", "Legal and regulatory obligations", "Legal obligation; legitimate interests"],
          ["Send marketing emails, where applicable", "To tell you about the product", "Consent, or legitimate interests where permitted, with opt-out in every message"],
        ]}
      />
      <P>
        Where we rely on legitimate interests, we have considered the interests, rights and freedoms of the
        people concerned. You may object to processing based on legitimate interests - see Section 11.
      </P>

      <H2 id="ai">6. Automated analysis and AI</H2>
      <P>
        RepCheck uses automated systems, including third-party large language models, to analyse the text and
        images of public posts and to produce a risk score, risk categories and a short explanation. Some
        content is additionally screened by an automated content-moderation classifier.
      </P>
      <UL>
        <li><strong>Accuracy.</strong> Automated analysis is probabilistic. It produces false positives and false negatives, can misread satire, quotation, reclaimed language, dialect and context, and may be less accurate in some languages. Output must be reviewed by a person before any decision is made.</li>
        <li><strong>No automated decisions with legal effect.</strong> RepCheck does not use this analysis to make decisions that produce legal effects concerning a person or similarly significantly affect them. It surfaces information for a human to review. Our customers must not use RepCheck output as the sole basis for decisions about employment, contracts, credit, housing or comparable matters.</li>
        <li><strong>Model training.</strong> We use commercial AI providers through their business APIs and do not permit them to use content submitted through the Service to train their general-purpose models. We do not sell content to AI developers.</li>
        <li><strong>Human review.</strong> If a risk score materially affects you and you want a human to look at it, contact us at <a href="mailto:privacy@getrepcheck.com" className="text-brand-600 underline underline-offset-2 hover:text-brand-700">privacy@getrepcheck.com</a>.</li>
      </UL>

      <H2 id="sharing">7. Sharing and sub-processors</H2>
      <P>
        We do not sell personal information, and we do not share it for cross-context behavioural advertising.
        We share information only as described here:
      </P>
      <Table
        head={["Recipient", "What they receive", "Where they process it"]}
        rows={[
          ["AI analysis provider", "Text and images from public posts being analysed", "United States"],
          ["Email delivery provider", "Recipient email address and message content", "United States"],
          ["Cloud hosting provider", "All data stored by the Service, on servers we manage", "European Union (France)"],
          ["Payment provider (where paid plans apply)", "Billing details you provide to them directly", "Varies by provider"],
          ["Professional advisers, acquirers, authorities", "Only as needed, or as legally required", "Varies"],
        ]}
      />
      <P>
        We can tell you exactly who these providers are:{" "}
        <a href="mailto:privacy@getrepcheck.com" className="text-brand-600 underline underline-offset-2 hover:text-brand-700">email us</a>{" "}
        and we will send you our current list of named sub-processors. Business customers receive that list, and
        advance notice of any change to it, under their data processing agreement.
      </P>
      <P>
        Our databases are operated by us on infrastructure we control; we do not use a third-party managed
        database provider. We require sub-processors to protect personal information and to process it only for
        the purposes we specify. <strong>Our AI provider does not use content submitted through RepCheck to train
        its models.</strong> We will publish material changes to the categories above in this policy.
      </P>

      <H2 id="transfers">8. International transfers</H2>
      <P>
        RepCheck operates from the United States, and the servers that store your account data, monitored-account
        records and scan results are located in <strong>France, in the European Union</strong>. Personal
        information is therefore stored in the EU, and is accessed from the United States by our personnel for
        support, security and administration.
      </P>
      <P>
        Some sub-processors are located in the United States - in particular our AI analysis provider and our
        email delivery provider. Content submitted for analysis and email addresses are therefore transferred
        to the United States. Where we transfer personal information out of the European Economic Area or the
        United Kingdom, we rely on appropriate safeguards, such as the European Commission's Standard Contractual
        Clauses (and the UK Addendum where relevant), together with additional measures where required. You can
        request details of the safeguards that apply using the contact details below.
      </P>

      <H2 id="retention">9. Data retention</H2>
      <UL>
        <li><strong>Account data</strong> - kept while your account is active, and for a reasonable period afterwards to meet legal, accounting and dispute-resolution needs.</li>
        <li><strong>Monitoring data (accounts, posts, analysis results, alerts, reports)</strong> - kept for as long as your workspace requires it to provide history and reporting, or until you or your customer deletes it. When you delete a client, account or scan, the associated records are deleted from our production systems.</li>
        <li><strong>Logs</strong> - kept for a limited period for security and troubleshooting.</li>
        <li><strong>Backups</strong> - residual copies may persist in backups for a limited period before being overwritten.</li>
      </UL>
      <P>
        If you want your data deleted sooner, contact us and we will action it subject to our legal obligations.
      </P>

      <H2 id="security">10. Security</H2>
      <P>
        We take reasonable and appropriate technical and organisational measures to protect personal
        information, including:
      </P>
      <UL>
        <li>encryption of traffic in transit using TLS;</li>
        <li>passwords stored only as salted hashes;</li>
        <li>authentication via HttpOnly, Secure session cookies;</li>
        <li>role-based access control and least-privilege access to production systems;</li>
        <li>network isolation between services and restricted administrative access.</li>
      </UL>
      <P>
        No service can be guaranteed completely secure. We do not currently hold a SOC 2 or ISO 27001
        certification, and we will not claim otherwise. If we become aware of a personal data breach affecting
        you, we will notify you and the relevant supervisory authority where the law requires it.
      </P>

      <H2 id="your-rights">11. Your privacy rights</H2>
      <P>
        Depending on where you live, you may have some or all of the following rights: to access a copy of your
        personal information; to correct inaccurate information; to delete information; to restrict or object to
        processing; to data portability; and to withdraw consent at any time without affecting prior processing.
      </P>
      <H3>European Economic Area and United Kingdom</H3>
      <P>
        You may exercise the rights above and lodge a complaint with your local supervisory authority - in the
        UK, the Information Commissioner's Office. We would appreciate the chance to address your concern first.
      </P>
      <H3>California</H3>
      <P>
        Under the CCPA/CPRA you may request to know, delete, and correct personal information, and to opt out of
        sale or sharing. <strong>RepCheck does not sell personal information and does not share it for
        cross-context behavioural advertising.</strong> We will not discriminate against you for exercising your
        rights. You may use an authorised agent.
      </P>
      <H3>Nigeria and other regions</H3>
      <P>
        Where the Nigeria Data Protection Act or a comparable law applies, we honour the equivalent rights it
        provides.
      </P>
      <P>
        To exercise any right, email <a href="mailto:privacy@getrepcheck.com" className="text-brand-600 underline underline-offset-2 hover:text-brand-700">privacy@getrepcheck.com</a>. We will verify your request and respond within
        the period required by law. Requests are free unless they are manifestly unfounded or excessive.
      </P>

      <H2 id="monitored-rights">12. Rights of monitored individuals</H2>
      <P>
        If your public social media activity has been collected through RepCheck at the direction of one of our
        customers, you still have rights over that information. This applies whether you are the account being
        monitored or someone who replied publicly to one of its posts.
      </P>
      <UL>
        <li>Where our customer is the controller, we will refer your request to them and support them in responding. We will tell you that we have done so where we are permitted to.</li>
        <li>You may ask us to stop processing information about you, to correct it, or to delete it. We will action or forward your request without undue delay.</li>
        <li>You may object to processing based on legitimate interests.</li>
      </UL>
      <P>
        Email <a href="mailto:privacy@getrepcheck.com" className="text-brand-600 underline underline-offset-2 hover:text-brand-700">privacy@getrepcheck.com</a> with enough detail for us to locate the records - normally the platform
        and the account handle.
      </P>

      <H2 id="cookies">13. Cookies and similar technologies</H2>
      <P>
        RepCheck uses <strong>strictly necessary cookies only</strong>. We set an authentication cookie
        (<code className="rounded bg-muted px-1 py-0.5 text-[13px]">auth_token</code>) so that you stay signed
        in, and we store limited data in your browser's local storage to keep the application working between
        page loads. We do not use advertising, analytics or tracking cookies, so we do not present a consent
        banner for them. Blocking the authentication cookie will prevent you from signing in.
      </P>

      <H2 id="children">14. Children</H2>
      <P>
        The Service is intended for business use by adults. It is not directed to children, and we do not
        knowingly collect personal information from children under 16 as account holders. If you believe a child
        has provided us with personal information as a user of the Service, contact us and we will delete it.
      </P>

      <H2 id="changes">15. Changes to this policy</H2>
      <P>
        We may update this policy as the Service evolves. We will change the "Last updated" date above, and for
        material changes we will provide additional notice - for example by email or an in-product notice -
        before the change takes effect where required.
      </P>

      <H2 id="contact">16. Contact us</H2>
      <P>
        Questions, requests or complaints: <a href="mailto:privacy@getrepcheck.com" className="text-brand-600 underline underline-offset-2 hover:text-brand-700">privacy@getrepcheck.com</a>.
        We aim to acknowledge every request within five business days. If we appoint a data protection officer or
        an EU/UK representative, or if our postal contact details change, we will publish them here.
      </P>
    </LegalLayout>
  );
}
